import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Printer, AlertTriangle, ArrowRight, ExternalLink, FileText, BookOpen, ChevronRight, Download } from "lucide-react";
import ReactMarkdown from "react-markdown";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Section definitions for the scenario page.
 * `kind` controls how the section renders.
 * If `optional` is true, the section is hidden when its data is empty.
 */

// Category-level framing notes that appear once at the top of every scenario
// in a given category. These are NOT replacements for per-scenario content —
// they surface guidance that applies across all situations in the category so
// it doesn't need to be repeated inside every scenario.
const CATEGORY_NOTES = {
  "Emotional & Human Moments": {
    label: "A note across this category",
    body: "Across all emotional and human moment situations you'll run into, it is always recommended to know your company policies, benefits, and procedures — and how those might impact the path forward. It is also always recommended to maintain a strong relationship with your own manager and HR so they can act as a support partner and guide in these situations.",
  },
};

// Per-section-kind panel treatment. Subtle gradient backgrounds drawn from
// the brand palette (teal / plum / sage / gold — no navy) so each
// section gets gentle color identity as the reader scrolls. Set to null for
// kinds that handle their own background (helpful-grouped, harder, support,
// clear-model, resources, reminder).
const SECTION_PANELS = {
  "paragraph-list": {
    background:
      "linear-gradient(135deg, rgba(42, 110, 115, 0.055), rgba(42, 110, 115, 0.015))",
    border: "1px solid rgba(42, 110, 115, 0.10)",
  },
  "plum-bullets": {
    background:
      "linear-gradient(135deg, rgba(140, 99, 120, 0.055), rgba(140, 99, 120, 0.015))",
    border: "1px solid rgba(140, 99, 120, 0.10)",
  },
  "teal-bullets": {
    background:
      "linear-gradient(135deg, rgba(42, 110, 115, 0.055), rgba(42, 110, 115, 0.015))",
    border: "1px solid rgba(42, 110, 115, 0.10)",
  },
  "numbered": {
    background:
      "linear-gradient(135deg, rgba(74, 158, 139, 0.07), rgba(74, 158, 139, 0.015))",
    border: "1px solid rgba(74, 158, 139, 0.14)",
  },
  "questions": {
    background:
      "linear-gradient(135deg, rgba(154, 159, 141, 0.10), rgba(154, 159, 141, 0.025))",
    border: "1px solid rgba(154, 159, 141, 0.18)",
  },
  "follow-grouped": {
    background:
      "linear-gradient(135deg, rgba(42, 110, 115, 0.055), rgba(42, 110, 115, 0.015))",
    border: "1px solid rgba(42, 110, 115, 0.10)",
  },
};

// "after" kind is rendered via teal-bullets; give it a distinct sage tint
// for visual contrast against the earlier teal-bullets section ("matters").
// We resolve this in the outer wrapper based on section id, not kind.
const SECTION_PANEL_OVERRIDES_BY_ID = {
  after: {
    background:
      "linear-gradient(135deg, rgba(154, 159, 141, 0.10), rgba(154, 159, 141, 0.025))",
    border: "1px solid rgba(154, 159, 141, 0.18)",
  },
};

// Brand accent per scenario category — kept in sync with Library.jsx so the
// left-edge color rule on a "See also" / "More from" mini-card matches the
// color the same scenario shows on the Library page. Strictly from the
// Manage(Meant) Excellence palette (teal / plum / sage / coral / gold / gray).
const CATEGORY_ACCENT = {
  "Managing Performance & Accountability": { edge: "var(--mme-teal)",     wash: "rgba(8, 114, 128, 0.045)" },
  "Emotional & Human Moments":              { edge: "var(--mme-plum)",     wash: "rgba(136, 98, 119, 0.045)" },
  "Team Dynamics & Relationships":          { edge: "var(--mme-sage)",     wash: "rgba(74, 158, 139, 0.045)" },
  "Communication & Transparency":           { edge: "var(--mme-teal-soft)",wash: "rgba(63, 142, 153, 0.045)" },
  "Identity, Authority & Leadership Presence": { edge: "var(--mme-coral)", wash: "rgba(244, 125, 95, 0.045)" },
  "Career & Transitions":                   { edge: "#1F4D5A",             wash: "rgba(31, 77, 90, 0.045)" },
  "Managing Up & Organizational Reality":   { edge: "var(--mme-gray)",     wash: "rgba(108, 109, 112, 0.045)" },
  "Self-Management for Managers":           { edge: "var(--mme-plum-soft)",wash: "rgba(168, 133, 151, 0.045)" },
  "Managing Change":                        { edge: "var(--mme-sage-deep)",wash: "rgba(53, 122, 107, 0.045)" },
};
const DEFAULT_ACCENT = { edge: "var(--mme-teal)", wash: "rgba(8, 114, 128, 0.045)" };

const SECTIONS = [
  { id: "context", label: "General context to consider", field: "general_context", kind: "paragraph-list", optional: true },
  { id: "why", label: "Why this can be challenging", field: "why_challenging", kind: "plum-bullets" },
  { id: "matters", label: "What matters most", field: "what_matters_most", kind: "teal-bullets" },
  { id: "frameworks", label: "Frameworks for this scenario", field: "frameworks", kind: "frameworks", optional: true },
  { id: "resources", label: "Further reading from Manage(Meant) Excellence", field: "resources", kind: "resources", optional: true },
  { id: "immediate", label: "Immediate considerations", field: "immediate_considerations", kind: "numbered" },
  { id: "ground", label: "Ground yourself before the conversation", field: "ground_yourself", kind: "questions", optional: true },
  { id: "helpful", label: "Helpful language", field: "helpful_language", kind: "helpful-grouped", optional: true },
  { id: "harder", label: "Common instincts to avoid", field: "what_makes_harder", kind: "harder" },
  { id: "support", label: "Additional support may be needed when", field: "escalation_considerations", kind: "support", optional: true },
  { id: "follow", label: "Follow through", field: "follow_through", kind: "follow-grouped" },
  { id: "after", label: "After the conversation", field: "after_conversation", kind: "teal-bullets", optional: true },
  { id: "reminder", label: "Manager reminder", field: "manager_reminder", kind: "reminder", optional: true },
];

function isEmpty(val) {
  if (val == null) return true;
  if (Array.isArray(val)) {
    if (val.length === 0) return true;
    // Grouped lists are "empty" if every group has no items.
    if (typeof val[0] === "object" && val[0] !== null) {
      // Handle both HelpfulLanguageGroup-like ({label,items}) and CLEAR steps
      // ({letter,name,description,helpful_language}). For CLEAR, a step is
      // considered "present" if it has a letter or name.
      return val.every((g) => {
        if ("items" in g) return !g.items || g.items.length === 0;
        if ("letter" in g || "name" in g) return !(g.letter || g.name);
        // BoundaryStep legacy: only "label" key. Present if label is non-empty.
        if ("label" in g && !("steps" in g) && !("title" in g)) return !g.label;
        // Framework: present if it has any step with content, or a non-empty title.
        if ("steps" in g || "title" in g) {
          if (g.title && String(g.title).trim()) return false;
          const steps = g.steps || [];
          return steps.every((s) => !(s.label || s.title || s.description));
        }
        return false;
      });
    }
    return false;
  }
  if (typeof val === "string") return val.trim().length === 0;
  return false;
}

// Render `[bracketed text]` segments with a subtle gold dotted underline
// so managers can spot the places they need to personalize. Also detects
// HR / company-policy phrases and tags them inline with a small gold
// AlertTriangle marker (legend rendered once at the top of the scenario).
const PLACEHOLDER_SPLIT_RE = /(\[[^\]\n[]+\])/g;
const PLACEHOLDER_MATCH_RE = /^\[[^\]\n[]+\]$/;
// Split (capturing) regex used to break a text run on HR phrases.
const HR_SPLIT_RE = /(\bHR\b|\bhuman resources\b|\bcompany polic(?:y|ies)\b|\bcompany guidelines?\b|\bemployee handbook\b|\bcompliance\b)/gi;
// Non-global match regex used to identify which pieces are HR phrases.
const HR_TEST_RE = /^(?:HR|human resources|company polic(?:y|ies)|company guidelines?|employee handbook|compliance)$/i;
// True if any reachable scenario text contains an HR / policy phrase.
// Used to conditionally render the legend banner near the top of the page.
const HR_SCAN_RE = /\bHR\b|\bhuman resources\b|\bcompany polic(?:y|ies)\b|\bcompany guidelines?\b|\bemployee handbook\b|\bcompliance\b/i;
function scenarioMentionsHR(scenario) {
  if (!scenario) return false;
  const fields = [
    scenario.tagline,
    ...(scenario.general_context || []),
    ...(scenario.why_challenging || []),
    ...(scenario.what_matters_most || []),
    ...(scenario.immediate_considerations || []),
    ...(scenario.ground_yourself || []),
    ...(scenario.what_makes_harder || []),
    ...(scenario.escalation_considerations || []),
    ...(scenario.after_conversation || []),
    scenario.manager_reminder,
  ];
  for (const grp of scenario.helpful_language || []) {
    fields.push(grp.label, ...(grp.items || []));
  }
  for (const grp of scenario.follow_through || []) {
    fields.push(grp.label, ...(grp.items || []));
  }
  for (const fw of scenario.frameworks || []) {
    fields.push(fw.title, fw.subtitle);
    for (const step of fw.steps || []) {
      fields.push(step.title, step.description, ...(step.examples || []));
    }
  }
  return fields.some((s) => typeof s === "string" && HR_SCAN_RE.test(s));
}
const HRMarker = () => (
  <span
    className="inline-flex items-center align-middle"
    style={{ marginLeft: "4px", lineHeight: 0 }}
    title="Consult HR or your company policies"
    aria-label="Consult HR or your company policies"
    data-testid="hr-marker"
  >
    <AlertTriangle
      size={13}
      strokeWidth={2.2}
      style={{ color: "var(--mme-gold-deep)", display: "inline-block", marginBottom: "-1px" }}
    />
  </span>
);
function withPlaceholders(text) {
  if (typeof text !== "string" || !text) return text;
  const placeholderParts = text.split(PLACEHOLDER_SPLIT_RE);
  const out = [];
  let key = 0;
  for (const part of placeholderParts) {
    if (PLACEHOLDER_MATCH_RE.test(part)) {
      out.push(
        <span
          key={key++}
          className="ph-fill"
          title="Manager: fill in with your specific detail"
          data-testid="scenario-placeholder"
        >
          {part}
        </span>
      );
      continue;
    }
    if (!part) continue;
    const hrParts = part.split(HR_SPLIT_RE);
    for (const piece of hrParts) {
      if (!piece) continue;
      if (HR_TEST_RE.test(piece)) {
        out.push(
          <span key={key++} style={{ whiteSpace: "nowrap" }}>
            {piece}
            <HRMarker />
          </span>
        );
      } else {
        out.push(<React.Fragment key={key++}>{piece}</React.Fragment>);
      }
    }
  }
  return out.length ? out : text;
}

const Bullets = ({ items, accent }) => (
  <ul className="space-y-4 list-none">
    {items.map((item, i) => {
      if (isProTip(item)) {
        return <ProTip key={i} text={item} />;
      }
      return (
        <li key={i} className="flex gap-4">
          <span className="shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
          <span>{withPlaceholders(item)}</span>
        </li>
      );
    })}
  </ul>
);

/**
 * isProTip — true when an editor has marked a list/paragraph item with a
 * leading "Pro tip:" (case-insensitive, allows curly apostrophes / dashes).
 * Matches the convention used throughout the seed data.
 */
const PRO_TIP_RE = /^\s*pro[\s-]*tip\s*[:\-—]/i;
function isProTip(text) {
  return typeof text === "string" && PRO_TIP_RE.test(text);
}
function stripProTipLabel(text) {
  return (text || "").replace(PRO_TIP_RE, "").trim();
}

/**
 * ProTip — a callout that "pops" inside any section without leaving the
 * surrounding box. Uses the brand gold accent so it reads as a highlight
 * (separate from the deeper teal Manager Reminder), with an italicized
 * serif eyebrow and a tinted background tied to the cream paper tone.
 */
function ProTip({ text }) {
  return (
    <div
      className="rounded-xl px-5 py-4 md:px-6 md:py-5"
      style={{
        background: "rgba(217, 184, 106, 0.12)",
        border: "1px solid rgba(217, 184, 106, 0.45)",
        borderLeft: "4px solid var(--mme-gold)",
      }}
      data-testid="pro-tip"
    >
      <p
        className="text-[11px] font-bold uppercase tracking-[0.22em] mb-2"
        style={{ color: "var(--mme-teal-deep, var(--mme-teal))" }}
      >
        Pro tip
      </p>
      <p className="leading-relaxed" style={{ color: "var(--mek-text)" }}>
        {withPlaceholders(stripProTipLabel(text))}
      </p>
    </div>
  );
}

function SectionContent({ section, scenario }) {
  const value = scenario[section.field];
  switch (section.kind) {
    case "paragraph-list":
      return (
        <div className="space-y-5 leading-relaxed">
          {value.map((p, i) => {
            if (isProTip(p)) return <ProTip key={i} text={p} />;
            return <p key={i}>{withPlaceholders(p)}</p>;
          })}
        </div>
      );
    case "plum-bullets":
      return <Bullets items={value} accent="var(--mme-plum)" />;
    case "teal-bullets":
      return <Bullets items={value} accent="var(--mme-teal)" />;
    case "numbered":
      return (
        <ol className="space-y-3 list-none">
          {value.map((step, i) => {
            if (isProTip(step)) return <ProTip key={i} text={step} />;
            return (
              <li key={i} className="flex gap-4 leading-relaxed">
                <span
                  className="shrink-0 font-semibold tabular-nums"
                  style={{ color: "var(--mme-teal-deep)", minWidth: "1.75rem" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{withPlaceholders(step)}</span>
              </li>
            );
          })}
        </ol>
      );
    case "questions":
      return (
        <div className="space-y-3">
          <p className="leading-relaxed" style={{ color: "var(--mek-text-2)" }}>
            Pause and ask yourself:
          </p>
          <ul className="space-y-3 list-none">
            {value.map((q, i) => {
              if (isProTip(q)) return <ProTip key={i} text={q} />;
              return (
                <li
                  key={i}
                  className="flex gap-3 leading-relaxed"
                  style={{ color: "var(--mek-text)" }}
                >
                  <span className="shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full" style={{ background: "var(--mme-sage, var(--mme-teal))" }} />
                  <span>{withPlaceholders(q)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      );
    case "helpful-grouped":
      return (
        <div className="p-7 md:p-9 rounded-xl divide-y" style={{ background: "rgba(42, 110, 115, 0.08)", borderColor: "rgba(42, 110, 115, 0.20)" }}>
          {value.map((group, gi) => (
            <div
              key={gi}
              className={`${gi === 0 ? "pb-6" : "py-6"} ${gi === value.length - 1 ? "pb-0" : ""}`}
            >
              {group.label && (
                <p
                  className="font-semibold mb-3"
                  style={{ color: "var(--mme-teal-deep)" }}
                >
                  {group.label}
                </p>
              )}
              <ul className="space-y-3 list-none">
                {(group.items || []).map((q, i) => {
                  if (isProTip(q)) return <ProTip key={i} text={q} />;
                  return (
                    <li key={i} className="flex gap-3 leading-relaxed" style={{ color: "var(--mek-text)" }}>
                      <span className="shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full" style={{ background: "var(--mme-teal)" }} />
                      <span>"{withPlaceholders(q)}"</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      );
    case "harder":
      return (
        <div className="p-7 md:p-9 rounded-xl" style={{ background: "rgba(140, 99, 120, 0.08)" }}>
          <ul className="space-y-4 list-none">
            {value.map((q, i) => {
              if (isProTip(q)) return <ProTip key={i} text={q} />;
              return (
                <li key={i} className="flex gap-4 leading-relaxed">
                  <span
                    className="shrink-0 mt-2"
                    style={{
                      width: "10px",
                      height: "10px",
                      background: "var(--mme-plum)",
                      transform: "rotate(45deg)",
                      borderRadius: "1px",
                    }}
                    aria-hidden="true"
                  />
                  <span>{withPlaceholders(q)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      );
    case "support":
      return (
        <div className="flex items-start gap-4 p-7 md:p-9 rounded-xl" style={{ background: "var(--mme-cream)", border: "1px solid rgba(229, 197, 127, 0.55)" }}>
          <AlertTriangle size={22} className="mt-1 shrink-0" style={{ color: "var(--mme-gold-deep)" }} />
          <div className="flex-1">
            <ul className="space-y-3">
              {value.map((item, i) => {
                if (isProTip(item)) return <ProTip key={i} text={item} />;
                return (
                  <li key={i} className="flex gap-3">
                    <span className="shrink-0 mt-2.5 w-1 h-1 rounded-full" style={{ background: "var(--mme-gold-deep)" }} />
                    <span>{withPlaceholders(item)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      );
    case "follow-grouped":
      return (
        <div className="divide-y" style={{ borderColor: "rgba(42, 110, 115, 0.20)" }}>
          {value.map((group, gi) => (
            <div
              key={gi}
              className={`grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 ${
                gi === 0 ? "pb-6" : "py-6"
              } ${gi === value.length - 1 ? "pb-0" : ""}`}
              style={{
                borderColor: "rgba(42, 110, 115, 0.20)",
              }}
            >
              <div className="sm:col-span-3">
                <p
                  className="font-semibold leading-snug"
                  style={{ color: "var(--mme-teal-deep)" }}
                >
                  {group.label}
                </p>
              </div>
              <div className="sm:col-span-9">
                <ul className="space-y-3 list-none">
                  {(group.items || []).map((q, i) => {
                    if (isProTip(q)) return <ProTip key={i} text={q} />;
                    return (
                      <li key={i} className="flex gap-3 leading-relaxed">
                        <span className="shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full" style={{ background: "var(--mme-teal)" }} />
                        <span>{withPlaceholders(q)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ))}
        </div>
      );
    case "frameworks":
      return (
        <div className="space-y-12">
          {value.map((framework, fi) => (
            <div key={fi} data-testid={`framework-${fi}`}>
              {framework.title && (
                <h3
                  className="text-xl md:text-[22px] font-semibold leading-snug mb-2"
                  style={{ color: "var(--mek-text)" }}
                >
                  {framework.title}
                </h3>
              )}
              {framework.subtitle && (
                <p
                  className="italic leading-relaxed max-w-3xl mb-6"
                  style={{ color: "var(--mek-text-2)" }}
                >
                  {framework.subtitle}
                </p>
              )}
              <div className="space-y-4">
                {(framework.steps || []).map((step, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 p-6 sm:p-7 rounded-2xl"
                    style={{
                      background: "#ffffff",
                      border: "1px solid var(--mek-border)",
                      boxShadow: "0 6px 20px rgba(31, 85, 89, 0.04)",
                    }}
                    data-testid={`framework-${fi}-step-${i}`}
                  >
                    <div className="sm:col-span-2 flex sm:block items-baseline gap-3">
                      <p
                        className="font-semibold leading-none"
                        style={{
                          color: "var(--mme-teal-deep)",
                          fontSize: "clamp(28px, 3.4vw, 36px)",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {step.label || (i + 1)}
                      </p>
                      <p
                        className="sm:mt-2 text-sm"
                        style={{ color: "var(--mek-text-3)" }}
                      >
                        Step {i + 1} of {framework.steps.length}
                      </p>
                    </div>
                    <div className="sm:col-span-10">
                      {step.title && (
                        <h4
                          className="text-lg md:text-xl font-semibold leading-snug mb-3"
                          style={{ color: "var(--mek-text)" }}
                        >
                          {step.title}
                        </h4>
                      )}
                      {step.description &&
                        step.description.split(/\n\n+/).map((para, pi) => (
                          <p
                            key={pi}
                            className="text-prose leading-relaxed mb-3 last:mb-0"
                          >
                            {withPlaceholders(para)}
                          </p>
                        ))}
                      {step.examples && step.examples.length > 0 && (
                        <div
                          className="mt-5 p-5 rounded-xl"
                          style={{ background: "rgba(42, 110, 115, 0.08)" }}
                        >
                          <p
                            className="font-semibold mb-3"
                            style={{ color: "var(--mme-teal-deep)" }}
                          >
                            Examples
                          </p>
                          <ul className="space-y-2.5">
                            {step.examples.map((ex, ei) => (
                              <li
                                key={ei}
                                className="flex gap-3 leading-relaxed"
                                style={{ color: "var(--mek-text)" }}
                              >
                                <span
                                  className="shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full"
                                  style={{ background: "var(--mme-teal)" }}
                                />
                                <span>{withPlaceholders(ex)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );

    case "resources":
      return (
        <div className="space-y-3">
          {value.map((r, i) => {
            const Icon = r.kind === "template" ? FileText : r.kind === "tool" ? BookOpen : BookOpen;
            const kindLabel = r.kind === "template" ? "Template" : r.kind === "tool" ? "Tool" : "Article";
            return (
              <a
                key={i}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-5 p-6 rounded-xl transition-colors hover:bg-[color:var(--mek-bg-2)]"
                style={{ border: "1px solid var(--mek-border)", background: "#ffffff" }}
                data-testid={`resource-link-${i}`}
              >
                <span
                  className="shrink-0 flex items-center justify-center w-11 h-11 rounded-full"
                  style={{ background: "rgba(42, 110, 115, 0.10)", color: "var(--mme-teal)" }}
                >
                  <Icon size={18} strokeWidth={1.5} />
                </span>
                <span className="flex-1">
                  <span
                    className="block text-[10px] font-bold uppercase tracking-[0.22em] mb-1"
                    style={{ color: "var(--mme-teal)" }}
                  >
                    {kindLabel} · managemeantexcellence.com
                  </span>
                  <span
                    className="block font-serif text-xl md:text-[22px] leading-snug mb-1"
                    style={{ color: "var(--mek-text)" }}
                  >
                    {r.label}
                  </span>
                  {r.description && (
                    <span className="block text-prose text-sm md:text-[15px] leading-relaxed">
                      {r.description}
                    </span>
                  )}
                </span>
                <ExternalLink
                  size={14}
                  className="shrink-0 mt-1 transition-transform group-hover:translate-x-0.5"
                  style={{ color: "var(--mek-text-3)" }}
                />
              </a>
            );
          })}
        </div>
      );
    case "reminder":
      return (
        <div className="p-8 md:p-10 rounded-2xl" style={{ background: "var(--mme-teal)", color: "#ffffff", boxShadow: "0 18px 50px rgba(31, 85, 89, 0.18)" }}>
          <p className="text-xl md:text-[22px] font-medium leading-relaxed" style={{ color: "#ffffff" }}>
            {withPlaceholders(value)}
          </p>
        </div>
      );
    default:
      return null;
  }
}

export default function ScenarioDetail() {
  const { slug } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [allScenarios, setAllScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sidebar: which categories the user has collapsed. Default-collapsed
  // behavior is handled in render — every category is collapsed by default
  // EXCEPT the one the current scenario belongs to. The user can then toggle
  // anything they want open or closed; their preference is persisted across
  // scenario navigations via sessionStorage so the sidebar feels stable.
  const [openCategories, setOpenCategories] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = sessionStorage.getItem("mek.sidebar.open");
      return raw ? new Set(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  });
  const toggleCategory = (category) =>
    setOpenCategories((prev) => {
      // Lazily seed from the auto-open default (current scenario's category)
      // the first time the user clicks anything.
      const seeded = prev
        ? new Set(prev)
        : new Set(scenario?.category ? [scenario.category] : []);
      if (seeded.has(category)) seeded.delete(category);
      else seeded.add(category);
      try {
        sessionStorage.setItem(
          "mek.sidebar.open",
          JSON.stringify(Array.from(seeded))
        );
      } catch {
        /* sessionStorage may be unavailable; ignore */
      }
      return seeded;
    });

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.authenticated) return navigate("/access");
    setLoading(true);
    Promise.all([
      api.get(`/library/scenarios/${slug}`).then((r) => setScenario(r.data)),
      api.get(`/library/scenarios`).then((r) => setAllScenarios(r.data)),
    ])
      .catch(() => setScenario(null))
      .finally(() => setLoading(false));
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, [slug, auth, navigate]);

  const grouped = useMemo(() => {
    // Canonical taxonomy (must match backend CATEGORIES and Library.jsx).
    const CATEGORY_ORDER = [
      "Managing Performance & Accountability",
      "Emotional & Human Moments",
      "Team Dynamics & Relationships",
      "Communication & Transparency",
      "Identity, Authority & Leadership Presence",
      "Career & Transitions",
      "Managing Up & Organizational Reality",
      "Self-Management for Managers",
      "Managing Change",
    ];
    const map = new Map();
    for (const s of allScenarios) {
      if (!map.has(s.category)) map.set(s.category, []);
      map.get(s.category).push(s);
    }
    const ordered = [];
    for (const cat of CATEGORY_ORDER) {
      if (map.has(cat)) {
        ordered.push([cat, map.get(cat)]);
        map.delete(cat);
      }
    }
    for (const entry of map.entries()) ordered.push(entry);
    return ordered;
  }, [allScenarios]);

  // Up to 3 sibling scenarios in the same category — chosen by closest
  // editorial neighbor (display_order distance from the current scenario).
  // Ties (e.g., one before / one after at equal distance) fall back to
  // display_order ascending so the order reads consistently.
  const related = useMemo(() => {
    if (!scenario || !allScenarios.length) return [];
    const here = scenario.display_order ?? 1000;
    const siblings = allScenarios.filter(
      (s) => s.category === scenario.category && s.slug !== scenario.slug
    );
    return siblings
      .map((s) => ({ s, dist: Math.abs((s.display_order ?? 1000) - here) }))
      .sort((a, b) => {
        if (a.dist !== b.dist) return a.dist - b.dist;
        return (a.s.display_order ?? 1000) - (b.s.display_order ?? 1000);
      })
      .slice(0, 3)
      .map((entry) => entry.s);
  }, [scenario, allScenarios]);

  // Editor-curated "see also" scenarios — typically cross-category links to
  // related themes (e.g., a disengagement scenario pointing to burnout +
  // morale + defensiveness). Preserves the author's chosen order. Resolves
  // slugs to full preview cards using the library, and silently drops any
  // slugs that no longer exist.
  const seeAlso = useMemo(() => {
    if (!scenario?.see_also?.length || !allScenarios.length) return [];
    const bySlug = new Map(allScenarios.map((s) => [s.slug, s]));
    return scenario.see_also
      .map((slug) => bySlug.get(slug))
      .filter(Boolean)
      .filter((s) => s.slug !== scenario.slug);
  }, [scenario, allScenarios]);

  const visibleSections = useMemo(() => {
    if (!scenario) return [];
    return SECTIONS.filter((sec) => {
      if (!sec.optional) return true;
      return !isEmpty(scenario[sec.field]);
    });
  }, [scenario]);

  if (loading)
    return (
      <div className="App">
        <Nav />
        <div className="p-12 text-prose">Loading…</div>
      </div>
    );
  if (!scenario)
    return (
      <div className="App">
        <Nav />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <p className="font-serif text-3xl mb-4">Scenario not found.</p>
          <Link to="/library" className="btn-primary">Back to library</Link>
        </div>
        <Footer />
      </div>
    );

  return (
    <div className="App" data-testid="scenario-detail-page">
      <Nav />

      <article className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">
        <div className="no-print mb-8 flex items-center justify-between">
          <Link to="/library" className="text-sm flex items-center gap-2 text-prose hover:text-[color:var(--mme-teal)]" data-testid="scenario-back">
            <ArrowLeft size={14} /> Back to library
          </Link>
          <div className="flex items-center gap-5">
            <button
              onClick={async () => {
                if (!scenario?.slug) return;
                try {
                  const res = await api.get(`/library/scenarios/${scenario.slug}/pdf`, { responseType: "blob" });
                  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = blobUrl;
                  a.download = `mek-${scenario.slug}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
                } catch (e) {
                  alert("We couldn't generate the PDF right now. Please try again in a moment.");
                }
              }}
              className="text-sm flex items-center gap-2 text-prose hover:text-[color:var(--mme-teal)]"
              data-testid="scenario-download-pdf"
              title="Download this scenario as a PDF"
            >
              <Download size={14} /> Download PDF
            </button>
            <button onClick={() => window.print()} className="text-sm flex items-center gap-2 text-prose hover:text-[color:var(--mme-teal)]" data-testid="scenario-print">
              <Printer size={14} /> Print
            </button>
          </div>
        </div>

        {/* Mobile picker */}
        <div className="md:hidden no-print mb-8 -mx-6 px-6 overflow-x-auto" data-testid="mobile-scenario-pills">
          <div className="flex gap-2 pb-2 min-w-max">
            {allScenarios.map((s) => {
              const active = s.slug === slug;
              return (
                <button
                  key={s.slug}
                  onClick={() => navigate(`/library/${s.slug}`)}
                  className="text-xs font-medium px-4 py-2 rounded-full whitespace-nowrap transition-colors"
                  style={{
                    background: active ? "var(--mme-teal)" : "var(--mek-bg-2)",
                    color: active ? "#ffffff" : "var(--mek-text-2)",
                    border: `1px solid ${active ? "var(--mme-teal)" : "var(--mek-border)"}`,
                  }}
                >
                  {s.title.replace(/^When (an? |the |someone |you |two )/i, "")}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-10 lg:gap-14">
          {/* Side nav */}
          <aside
            className="hidden md:block md:col-span-4 lg:col-span-3 no-print md:pl-5 md:pr-6 lg:pr-8 md:py-6 rounded-lg"
            style={{
              borderRight: "2px solid var(--mek-border)",
              background: "var(--mek-bg-2)",
            }}
            data-testid="scenarios-side-nav"
          >
            <nav className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
              <p
                className="text-sm font-bold uppercase tracking-[0.22em] mb-5 pb-4 border-b"
                style={{ color: "var(--mek-text-3)", borderColor: "var(--mek-border)" }}
              >
                All scenarios
              </p>
              {grouped.map(([category, items], gi) => {
                const catAccent = CATEGORY_ACCENT[category] || DEFAULT_ACCENT;
                const isOpen = openCategories
                  ? openCategories.has(category)
                  : category === scenario.category;
                return (
                <div key={category} className={gi > 0 ? "mt-5" : ""}>
                  <button
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center gap-2 mb-3 px-2 py-1 -ml-1 rounded-md hover:bg-[color:var(--mek-bg-2)] transition-colors text-left"
                    aria-expanded={isOpen}
                    data-testid={`side-nav-toggle-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <ChevronRight
                      size={14}
                      strokeWidth={2.5}
                      className="shrink-0 transition-transform duration-200"
                      style={{
                        color: catAccent.edge,
                        transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                      }}
                    />
                    <p
                      className="text-[11px] font-bold uppercase tracking-[0.2em] flex-1"
                      style={{ color: catAccent.edge }}
                    >
                      {category}
                    </p>
                    <span
                      className="text-[11px] tabular-nums"
                      style={{ color: "var(--mek-text-3)" }}
                      aria-hidden="true"
                    >
                      {items.length}
                    </span>
                  </button>
                  {isOpen && (
                    <div>
                      {items.map((s, i) => {
                        const active = s.slug === slug;
                        return (
                          <React.Fragment key={s.slug}>
                            {i > 0 && (
                              <div className="h-px mx-3" style={{ background: "var(--mek-border)" }} />
                            )}
                            <Link
                              to={`/library/${s.slug}`}
                              className="block py-3 px-3 rounded-md transition-colors"
                              style={{
                                background: active ? catAccent.edge : "transparent",
                                color: active ? "#ffffff" : "var(--mek-text)",
                              }}
                              data-testid={`side-nav-${s.slug}`}
                            >
                              <span
                                className="text-base leading-snug font-medium block"
                                style={{ color: active ? "#ffffff" : "var(--mek-text)" }}
                              >
                                {s.title}
                              </span>
                            </Link>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
                );
              })}
            </nav>
          </aside>

          {/* Content */}
          <div className="md:col-span-8 lg:col-span-9">
            <motion.header
              key={slug}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-12"
            >
              <p
                className="text-xs font-bold tracking-[0.22em] uppercase mb-5"
                style={{ color: "var(--mme-teal)" }}
              >
                {scenario.category}
              </p>
              <h1
                className="h-display text-4xl md:text-5xl lg:text-6xl mb-5 leading-[1.1]"
                data-testid="scenario-title"
              >
                {scenario.title}
              </h1>
              <p
                className="font-serif italic font-light text-xl md:text-2xl leading-snug"
                style={{ color: "var(--mek-text-2)" }}
              >
                {scenario.tagline}
              </p>
            </motion.header>

            {scenarioMentionsHR(scenario) && (
              <aside
                className="mb-10 flex items-start gap-3 px-5 py-4 rounded-xl"
                style={{
                  background: "var(--mme-cream)",
                  border: "1px solid rgba(229, 197, 127, 0.55)",
                }}
                data-testid="hr-legend"
              >
                <AlertTriangle
                  size={18}
                  strokeWidth={2.2}
                  className="shrink-0 mt-0.5"
                  style={{ color: "var(--mme-gold-deep)" }}
                />
                <p className="text-sm leading-relaxed" style={{ color: "var(--mek-text-2)" }}>
                  When you see this icon next to a phrase, it's recommended to
                  engage with HR or consult your company policies before
                  acting.
                </p>
              </aside>
            )}

            <div className="divider mb-12" />

            {CATEGORY_NOTES[scenario.category] && (
              <aside
                className="mb-14 p-7 md:p-9 rounded-2xl"
                style={{
                  background: "var(--mme-cream)",
                  border: "1px solid rgba(229, 197, 127, 0.55)",
                }}
                data-testid="category-note"
              >
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.22em] mb-4"
                  style={{ color: "var(--mme-gold-deep)" }}
                >
                  {CATEGORY_NOTES[scenario.category].label}
                </p>
                <p
                  className="font-serif text-lg md:text-xl leading-snug"
                  style={{ color: "var(--mek-text)" }}
                >
                  {CATEGORY_NOTES[scenario.category].body}
                </p>
              </aside>
            )}

            <div className="space-y-10">
              {visibleSections.map((sec) => {
                const panel = SECTION_PANEL_OVERRIDES_BY_ID[sec.id] || SECTION_PANELS[sec.kind] || null;
                return (
                  <section
                    key={sec.id}
                    data-testid={`section-${sec.id}`}
                    className={panel ? "rounded-2xl p-7 md:p-10" : ""}
                    style={panel || undefined}
                  >
                    <h2
                      className="text-2xl md:text-[24px] font-semibold mb-6"
                      style={{ color: "var(--mme-teal-deep)", letterSpacing: "-0.005em" }}
                    >
                      {sec.label}
                    </h2>
                    <div className="text-prose text-lg md:text-xl leading-relaxed">
                      <SectionContent section={sec} scenario={scenario} />
                    </div>
                  </section>
                );
              })}
            </div>

            {(scenario.extra_content || "").trim() && (
              <ExtraContent markdown={scenario.extra_content} />
            )}

            {seeAlso.length > 0 ? (
              <SeeAlsoScenarios items={seeAlso} />
            ) : (
              related.length > 0 && (
                <RelatedScenarios category={scenario.category} items={related} />
              )
            )}

            <ScenarioPrevNext slug={slug} all={allScenarios} navigate={navigate} />
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}

/**
 * ExtraContent — renders the optional, free-form per-scenario markdown
 * field. Used when standard sections don't cover everything (e.g. an
 * "Additional practical ways to support team morale" appendix). Styled to
 * match the rest of the page: serif headings via Cormorant, Manrope body,
 * teal accent on bullets/strong text.
 */
function ExtraContent({ markdown }) {
  return (
    <section className="mt-16 pt-12 border-t" style={{ borderColor: "var(--mek-border)" }}>
      <div className="text-prose text-lg md:text-xl leading-relaxed scenario-extra-content">
        <ReactMarkdown
          components={{
            h1: ({ node, ...p }) => (
              <h2
                className="h-display text-3xl md:text-4xl mt-10 mb-4 first:mt-0"
                style={{ color: "var(--mek-text)" }}
                {...p}
              />
            ),
            h2: ({ node, ...p }) => (
              <h3
                className="h-display text-2xl md:text-3xl mt-10 mb-3 first:mt-0"
                style={{ color: "var(--mek-text)" }}
                {...p}
              />
            ),
            h3: ({ node, ...p }) => (
              <h4
                className="font-semibold text-lg uppercase tracking-[0.12em] mt-7 mb-3"
                style={{ color: "var(--mme-teal-deep, var(--mme-teal))", fontFamily: 'Manrope, sans-serif' }}
                {...p}
              />
            ),
            h4: ({ node, ...p }) => (
              <h5
                className="font-semibold mt-6 mb-2"
                style={{ color: "var(--mek-text)" }}
                {...p}
              />
            ),
            p: ({ node, ...p }) => <p className="mb-4" {...p} />,
            strong: ({ node, ...p }) => <strong className="font-semibold" style={{ color: "var(--mek-text)" }} {...p} />,
            em: ({ node, ...p }) => <em className="italic" {...p} />,
            ul: ({ node, ...p }) => <ul className="space-y-2 mb-5 list-none pl-0" {...p} />,
            ol: ({ node, ...p }) => <ol className="space-y-2 mb-5 list-decimal pl-6" {...p} />,
            li: ({ node, children, ...p }) => (
              <li className="flex gap-3" {...p}>
                <span className="shrink-0 mt-3 w-1.5 h-1.5 rounded-full" style={{ background: "var(--mme-teal)" }} />
                <span>{children}</span>
              </li>
            ),
            a: ({ node, ...p }) => (
              <a
                className="underline"
                style={{ color: "var(--mme-teal-deep)", textUnderlineOffset: 3 }}
                target="_blank"
                rel="noopener noreferrer"
                {...p}
              />
            ),
          }}
        >
          {markdown}
        </ReactMarkdown>
      </div>
    </section>
  );
}


function SeeAlsoScenarios({ items }) {
  return (
    <aside
      className="no-print mt-16 pt-10 border-t"
      style={{ borderColor: "var(--mek-border)" }}
      data-testid="see-also-scenarios"
    >
      <div className="mb-7">
        <p
          className="text-[11px] font-bold uppercase tracking-[0.22em]"
          style={{ color: "var(--mme-gold-deep)" }}
        >
          See also
        </p>
        <p
          className="font-serif italic text-base md:text-lg mt-1 max-w-2xl"
          style={{ color: "var(--mek-text-2)" }}
        >
          These scenarios share themes with what you're navigating right now.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((s) => (
          <MiniScenarioCard key={s.slug} s={s} testid={`see-also-${s.slug}`} showCategory />
        ))}
      </div>
    </aside>
  );
}

function RelatedScenarios({ category, items }) {
  const accent = CATEGORY_ACCENT[category] || DEFAULT_ACCENT;
  return (
    <aside
      className="no-print mt-16 pt-10 border-t"
      style={{ borderColor: "var(--mek-border)" }}
      data-testid="related-scenarios"
    >
      <div className="flex items-baseline justify-between mb-7">
        <p
          className="text-[11px] font-bold uppercase tracking-[0.22em]"
          style={{ color: accent.edge }}
        >
          More from {category}
        </p>
        <p className="text-xs text-prose hidden sm:block">
          You may also reach for these.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((s) => (
          <MiniScenarioCard key={s.slug} s={s} testid={`related-${s.slug}`} />
        ))}
      </div>
    </aside>
  );
}

// Compact field-card used in the "See also" and "More from {category}" rails
// at the bottom of every scenario page. Visually consistent with the
// Library cards (cream paper, category-colored left-edge rule, accent eyebrow,
// "Open the kit" CTA) — just smaller / less padded.
function MiniScenarioCard({ s, testid, showCategory = false }) {
  const accent = CATEGORY_ACCENT[s.category] || DEFAULT_ACCENT;
  return (
    <Link
      to={`/library/${s.slug}`}
      className="block h-full group relative overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
      data-testid={testid}
      style={{
        background: "#FBF6E8",
        border: "1px solid var(--mek-border)",
        borderRadius: "10px",
        paddingTop: "22px",
        paddingBottom: "22px",
        paddingLeft: "24px",
        paddingRight: "22px",
      }}
    >
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0"
        style={{ width: "4px", background: accent.edge }}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ background: accent.wash }}
      />
      <div className="relative flex flex-col h-full">
        {showCategory && (
          <p
            className="text-[10px] font-bold uppercase tracking-[0.22em] mb-2"
            style={{ color: accent.edge }}
          >
            {s.category}
          </p>
        )}
        <p
          className="font-serif text-xl md:text-[22px] leading-tight mb-3"
          style={{ color: "var(--mek-text)" }}
        >
          {s.title}
        </p>
        <p className="text-prose text-sm mb-5 leading-relaxed">{s.tagline}</p>
        <span
          className="mt-auto text-[11px] font-semibold inline-flex items-center gap-1.5 transition-transform group-hover:translate-x-0.5 uppercase tracking-[0.14em]"
          style={{ color: accent.edge }}
        >
          Open the kit <ArrowRight size={11} strokeWidth={2.5} />
        </span>
      </div>
    </Link>
  );
}

function ScenarioPrevNext({ slug, all, navigate }) {
  const idx = all.findIndex((s) => s.slug === slug);
  if (idx === -1) return null;
  const prev = idx > 0 ? all[idx - 1] : null;
  const next = idx < all.length - 1 ? all[idx + 1] : null;
  if (!prev && !next) return null;
  return (
    <div className="no-print mt-16 pt-10 border-t flex items-center justify-between gap-4" style={{ borderColor: "var(--mek-border)" }}>
      <button
        onClick={() => prev && navigate(`/library/${prev.slug}`)}
        disabled={!prev}
        className="flex items-start gap-3 text-left hover:text-[color:var(--mme-teal)] disabled:opacity-30 disabled:cursor-not-allowed text-prose max-w-[45%]"
        data-testid="prev-scenario"
      >
        <ArrowLeft size={16} className="mt-1 shrink-0" />
        <span>
          <span className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--mek-text-3)" }}>Previous scenario</span>
          <span className="font-serif text-base md:text-lg leading-snug" style={{ color: "var(--mek-text)" }}>
            {prev ? prev.title : "Start of library"}
          </span>
        </span>
      </button>
      <button
        onClick={() => next && navigate(`/library/${next.slug}`)}
        disabled={!next}
        className="flex items-start gap-3 text-right hover:text-[color:var(--mme-teal)] disabled:opacity-30 disabled:cursor-not-allowed text-prose max-w-[45%]"
        data-testid="next-scenario"
      >
        <span>
          <span className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--mek-text-3)" }}>Next scenario</span>
          <span className="font-serif text-base md:text-lg leading-snug" style={{ color: "var(--mek-text)" }}>
            {next ? next.title : "End of library"}
          </span>
        </span>
        <ArrowRight size={16} className="mt-1 shrink-0" />
      </button>
    </div>
  );
}
